<template>
  <div
    class="registration-text-field"
    :class="{
      focused: isFocused,
      filled: hasValue,
      disabled
    }"
  >
    <input
      :id="fieldId"
      class="input-field"
      :value="modelValue"
      :type="type"
      :name="name"
      :inputmode="inputmode"
      :autocomplete="autocomplete"
      :maxlength="maxlength"
      :disabled="disabled"
      :readonly="readonly"
      :aria-label="placeholder"
      @input="handleInput"
      @focus="handleFocus"
      @blur="handleBlur"
    />

    <label
      class="input-label"
      :class="{ active: isFocused || hasValue }"
      :for="fieldId"
    >
      {{ placeholder }}
    </label>

    <div class="focus-glow"></div>
  </div>
</template>

<script setup>
import {
  computed,
  ref,
  useId
} from "vue";

const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: ""
  },

  placeholder: {
    type: String,
    default: ""
  },

  type: {
    type: String,
    default: "text"
  },

  name: {
    type: String,
    default: ""
  },

  inputmode: {
    type: String,
    default: undefined
  },

  autocomplete: {
    type: String,
    default: "off"
  },

  maxlength: {
    type: [String, Number],
    default: undefined
  },

  disabled: {
    type: Boolean,
    default: false
  },

  readonly: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits([
  "update:modelValue",
  "input",
  "focus",
  "blur"
]);

const isFocused = ref(false);

const generatedId = useId();

const fieldId = computed(() => {
  return `registration-field-${generatedId}`;
});

const hasValue = computed(() => {
  return String(props.modelValue ?? "").length > 0;
});

function handleInput(event) {
  const value = event.target.value;

  emit("update:modelValue", value);
  emit("input", event);
}

function handleFocus(event) {
  isFocused.value = true;
  emit("focus", event);
}

function handleBlur(event) {
  isFocused.value = false;
  emit("blur", event);
}
</script>

<style scoped>
.registration-text-field {
  position: relative;

  width: 330px;
  height: 50px;

  display: flex;
  align-items: center;

  box-sizing: border-box;
  overflow: hidden;

  padding: 0 15px;

  border: 1px solid var(--registration-field-border);
  border-radius: 10px;

  background:
    linear-gradient(
      180deg,
      var(--registration-field-bg-top) 0%,
      var(--registration-field-bg-bottom) 100%
    );

  box-shadow:
    inset 0 1px 0 var(--registration-field-highlight),
    0 4px 12px rgba(0, 0, 0, 0.05);

  transition:
    border-color 0.22s ease,
    box-shadow 0.22s ease,
    background 0.22s ease,
    opacity 0.22s ease,
    transform 0.22s ease;
}

.registration-text-field:hover:not(.disabled) {
  border-color: var(--registration-field-border-hover);
}

.registration-text-field.focused {
  border-color: rgba(40, 135, 247, 0.65);

  background:
    linear-gradient(
      180deg,
      var(--registration-field-focus-top) 0%,
      var(--registration-field-focus-bottom) 100%
    );

  box-shadow:
    inset 0 1px 0 var(--registration-field-highlight),
    0 0 0 4px rgba(40, 135, 247, 0.1),
    0 8px 20px rgba(33, 115, 229, 0.1);
}

.registration-text-field.disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.input-field {
  position: relative;
  z-index: 2;

  width: 100%;
  height: 100%;

  box-sizing: border-box;

  padding: 14px 0 0;

  border: none;
  outline: none;

  background: transparent;
  color: var(--registration-field-text);

  font-family: "Eastman-Light", sans-serif;
  font-size: 14px;

  caret-color: #2887f7;
}

.input-field:disabled {
  cursor: not-allowed;
}

.input-label {
  position: absolute;
  z-index: 2;

  left: 15px;
  top: 50%;

  max-width: calc(100% - 30px);

  overflow: hidden;

  color: var(--registration-label-color);

  font-family: "Eastman-Light", sans-serif;
  font-size: 14px;
  line-height: 1;

  white-space: nowrap;
  text-overflow: ellipsis;

  pointer-events: none;

  transform: translateY(-50%);
  transform-origin: left center;

  transition:
    top 0.22s ease,
    color 0.22s ease,
    font-size 0.22s ease,
    transform 0.22s ease;
}

.input-label.active {
  top: 11px;

  color: var(--registration-label-active);

  font-size: 9px;

  transform: translateY(0);
}

.registration-text-field.focused .input-label.active {
  color: #2887f7;
}

.focus-glow {
  position: absolute;

  top: -40px;
  right: -55px;

  width: 110px;
  height: 110px;

  border-radius: 50%;

  background: rgba(40, 135, 247, 0.15);

  filter: blur(34px);

  opacity: 0;

  pointer-events: none;

  transition: opacity 0.25s ease;
}

.registration-text-field.focused .focus-glow {
  opacity: 1;
}

.input-field:-webkit-autofill,
.input-field:-webkit-autofill:hover,
.input-field:-webkit-autofill:focus,
.input-field:-webkit-autofill:active {
  transition:
    background-color 5000s ease-in-out 0s;

  -webkit-text-fill-color:
    var(--registration-field-text) !important;

  box-shadow:
    0 0 0 1000px transparent inset !important;

  font-family:
    "Eastman-Light",
    sans-serif !important;

  caret-color:
    var(--registration-field-text);
}

@media (max-width: 768px) {
  .registration-text-field {
    width: 100%;
  }
}

@media (prefers-reduced-motion: reduce) {
  .registration-text-field,
  .input-label,
  .focus-glow {
    transition-duration: 0.01ms !important;
  }
}
</style>

<style>
:root {
  --registration-field-bg-top: #4a4a4a;
  --registration-field-bg-bottom: #444444;

  --registration-field-focus-top: #4a4c50;
  --registration-field-focus-bottom: #42454a;

  --registration-field-text: #ffffff;

  --registration-field-border:
    rgba(255, 255, 255, 0.05);

  --registration-field-border-hover:
    rgba(255, 255, 255, 0.11);

  --registration-field-highlight:
    rgba(255, 255, 255, 0.06);

  --registration-label-color: #c0c0c0;
  --registration-label-active: #929292;
}

[data-theme="light"] {
  --registration-field-bg-top: #eeeeee;
  --registration-field-bg-bottom: #e8e8e8;

  --registration-field-focus-top: #f3f7fd;
  --registration-field-focus-bottom: #edf3fb;

  --registration-field-text: #111111;

  --registration-field-border:
    rgba(0, 0, 0, 0.05);

  --registration-field-border-hover:
    rgba(0, 0, 0, 0.11);

  --registration-field-highlight:
    rgba(255, 255, 255, 0.75);

  --registration-label-color: #888888;
  --registration-label-active: #666666;
}
</style>